import httpService from '@/utils/httpService';
import store from '@/store';

/**
 * CML Bulk Import (Wave C-θ) — client → API wrapper.
 *
 * The Excel is parsed CLIENT-SIDE (see excelParser); this POSTs the pre-parsed
 * rows + a column mapping to the import endpoint and returns the structured
 * result ({ success, status, message, statistics, errors[], warnings[] }).
 * `status` is the server-derived verdict the dialog renders — Completed /
 * CompletedWithWarnings / PartiallyCompleted / Failed.
 *
 * Mirrors the cmlTaskLinks.js convention: focused module, httpService.post takes
 * a { url, data, headers } config, X-Facility-Id supplied via facilityHeaders().
 * The import endpoint is facility-scoped — the header is required (400 without it).
 * See [[project_ava_asset_facility_vocab]] and CML_BULK_IMPORT_BUILD_PLAN.md.
 */

/**
 * X-Facility-Id header from the current asset/facility context.
 * @returns {{ 'X-Facility-Id'?: string }}
 */
function facilityHeaders() {
  const assetId = store.state.assetId;
  return assetId ? { 'X-Facility-Id': assetId } : {};
}

/**
 * Import CMLs from pre-parsed Excel rows + a column mapping.
 * @param {object} payload
 * @param {Array<Object<string, *>>} payload.rows  Parsed Excel rows (key = column header).
 * @param {object} payload.mapping  CmlImportMapping: { fieldMappings, mandatoryFields, masterTagColumn, externalRefColumn, tmlPointNoColumn?, readingValueColumn?, readingDateColumn? }.
 * @param {string} payload.externalRefSource  Source system stamped on the upsert key (MERIDIUM/SAP/MAXIMO/ENGAGE/...).
 * @param {string} [payload.fileName]  Original file name (audit only).
 * @param {boolean} [payload.autoCreateInspections=false]  Create + link an inspection when the row's
 *   Inspection No. resolves to nothing. The server gates this on Inspection Tags.Create and 403s the
 *   whole import if the caller lacks it — send it only when the user really asked for it.
 * @returns {Promise<{ success: boolean, status: string, message: string, statistics: object, errors: Array, warnings: Array }>}
 */
export async function importCmls({ rows, mapping, externalRefSource, fileName, autoCreateInspections = false }) {
  const response = await httpService.post({
    url: '/api/v1/cmls/import',
    data: { rows, mapping, externalRefSource, fileName, autoCreateInspections: !!autoCreateInspections },
    headers: facilityHeaders(),
  });
  return response.data;
}
